import Vue from 'vue';
import VueTailwind from 'vue-tailwind';

import {
  TButton,
  TCard,
  TCheckbox,
  TDropdown,
  TInput,
  TModal,
  TRadio,
  TSelect,
  TTag,
} from 'vue-tailwind/dist/components';

const settings = {
  't-button': {
    component: TButton,
    props: {
      fixedClasses: `
        disabled:bg-primary-50
        disabled:cursor-not-allowed
        disabled:text-gray-300
        duration-100
        ease-in-out
        focus:outline-none
        focus:ring-0
        px-4
        py-2
        rounded
        transition
      `,
      classes: 'text-white bg-primary-500 border border-transparent shadow-sm hover:bg-primary-700',
      variants: {
        avatar: 'bg-secondary-50 border border-secondary-50 disabled:bg-transparent h-10 px-3 py-3 text-gray-500 text-sm rounded-full w-10',
        error: 'disabled:bg-red-50 text-white bg-red-500 border border-transparent shadow-sm hover:bg-red-600',
        icon: 'bg-transparent border-0 disabled:bg-transparent hover:bg-transparent px-0 py-0 shadow-none text-sm',
        success: 'text-white bg-grass-green-500 border border-transparent rounded shadow-sm hover:bg-primary-700',
        text: 'text-primary-500 hover:text-primary-700',
        outline: 'border text-primary-500 border-primary-500 rounded hover:bg-primary-500 hover:text-white disabled:border-primary-50',
      },
    },
  },
  't-card': {
    component: TCard,
    props: {
      fixedClasses: {
        body: 'pt-2 pb-6 px-4',
        footer: 'px-4 py-2 rounded-b',
        header: 'px-4 py-2 rounded-t',
        wrapper: 'border flex flex-col justify-between rounded',
      },
      classes: {
        body: 'text-sm text-gray-500',
        footer: 'text-black',
        header: 'text-lg text-black',
        wrapper: 'bg-white border-secondary-100 default-card-border',
      },
    },
  },
  't-checkbox': {
    component: TCheckbox,
    props: {
      fixedClasses: `
        transition
        duration-100
        ease-in-out
        rounded
        shadow-sm
        focus:outline-0
        focus:ring-0
        focus:ring-transparent
        focus:ring-offset-0
        focus:shadow-none
        disabled:opacity-50
      `,
      classes: 'text-primary-500',
    },
  },
  't-dropdown': {
    component: TDropdown,
    props: {
      fixedClasses: {
        button: `
          border-none
          border-transparent
          disabled:cursor-not-allowed
          disabled:opacity-50
          duration-100
          ease-in-out
          flex
          focus:outline-none
          focus:ring-0
          hover:text-gray-700
          items-center
          text-gray-500
          transition
        `,
        dropdown: `
          absolute
          bg-white
          bg-white
          border
          border-primary-50
          left-0
          mt-1
          origin-top-left
          rounded
          shadow
          shadow-md
          w-max
        `,
        dropdownWrapper: 'relative z-10',
        enterActiveClass: 'transition transform ease-out duration-100',
        enterClass: 'opacity-0 scale-95',
        enterToClass: 'opacity-100 scale-100',
        leaveActiveClass: 'transition transform ease-in duration-75',
        leaveClass: 'opacity-100 scale-100',
        leaveToClass: 'opacity-0 scale-95',
        wrapper: 'inline-flex flex-col',
      },
    },
  },
  't-input': {
    component: TInput,
    props: {
      fixedClasses: `
        border
        disabled:bg-light-gray-500
        disabled:border-none
        disabled:cursor-not-allowed
        disabled:text-black
        duration-100
        ease-in-out
        focus:outline-none
        focus:ring-0
        px-3
        py-2
        rounded
        shadow-sm
        sm:py-3
        transition
      `,
      classes: 'border-primary-50 placeholder-gray-300 text-black focus:border-primary-500',
      variants: {
        clear: `
          bg-transparent
          border-secondary-100
          border-l-0
          border-r-0
          border-t-0
          focus:border-secondary-300
          ring-0
          rounded-l-none
          rounded-r-none
          shadow-none
          text-black
        `,
        error: 'border-red-500 placeholder-red-500 text-red focus:border-red-500',
        success: 'border-green-500 placeholder-green-500 text-green focus:border-green-500',
      },
    },
  },
  't-modal': {
    component: TModal,
    props: {
      fixedClasses: {
        body: 'max-h-screen-1/2 overflow-y-auto',
        overlay: `
          bg-gray-900
          bg-opacity-50
          bottom-0
          fixed
          flex
          h-full
          items-center
          justify-center
          left-0
          overflow-auto
          right-0
          scrolling-touch
          top-0
          w-full
          z-40
        `,
        wrapper: 'sm:mx-4 sm:w-full w-modal-lg',
        modal: 'bg-white overflow-visible relative rounded',
        header: 'block rounded-t sm:px-4 w-full',
        footer: 'px-8 rounded-b sm:px-4',
        close: `
          absolute
          duration-100
          ease-in-out
          flex
          focus:outline-none
          focus:ring-0
          h-4
          hover:text-gray-100
          items-center
          justify-center
          right-6
          top-4
          transition
          w-4
        `,
      },
      classes: {
        body: 'px-8 py-4 sm:px-4 sm:py-5',
        close: 'text-white',
        footer: 'pb-4',
        header: 'bg-primary-500 px-8 py-4 sm:py-2 text-white',
      },
      variants: {
        banner: {
          body: 'py-2',
          close: 'text-gray-500',
          footer: 'bg-light-yellow pb-6 pt-2',
          header: 'pb-2 sm:px-0 text-primary-500',
          wrapper: 'sm:absolute sm:bottom-0 sm:mx-0',
        },
        clean: {
          body: 'py-2',
          close: 'text-gray-500',
          footer: 'bg-light-yellow pb-6 pt-2',
          header: 'px-8 py-2 text-primary-500',
          wrapper: 'sm:absolute sm:bottom-0 sm:mx-0',
        },
        focus: {
          body: 'px-8 py-4 sm:px-4 sm:py-5',
          close: 'text-white',
          footer: 'pb-4',
          header: 'bg-primary-500 px-8 py-4 sm:py-2 text-white',
          overlay: 'backdrop-blur-md',
        },
        error: {
          body: 'px-8 py-4 sm:px-4 sm:py-6',
          close: 'text-white',
          footer: 'pb-4 sm:pb-3',
          header: 'bg-red-500 px-8 py-4 sm:py-2 text-white',
        },
        normal: {
          wrapper: 'sm:mx-4 sm:w-full w-[448px]',
        },
        base: {
          wrapper: 'sm:mx-4 sm:w-full w-[448px]',
          body: 'p-4',
          footer: 'px-6 py-4',
          header: 'bg-primary-500 px-8 py-4 text-white',
          close: 'text-white top-6 w-6',
        },
      },
    },
  },
  't-radio': {
    component: TRadio,
    props: {
      wrapped: true,
      fixedClasses: {
        wrapper: 'flex items-center',
        label: 'ml-2 uppercase',
        inputWrapper: 'inline-flex items-center leading-none',
        input: `
          disabled:cursor-not-allowed
          disabled:opacity-50
          duration-100
          ease-in-out
          focus:border-primary-500
          focus:outline-none
          focus:ring-2
          focus:ring-primary-500
          focus:ring-offset-0
          focus:ring-opacity-50
          shadow-none
          transition
        `,
      },
      classes: {
        input: 'border-black text-primary-500',
        label: 'text-black w-full',
      },
    },
  },
  't-select': {
    component: TSelect,
    props: {
      wrapped: true,
      fixedClasses: {
        arrow: 'fill-current h-4 w-4',
        arrowWrapper: `
          absolute
          flex
          inset-y-0
          items-center
          pointer-events-none
          px-2
          right-0
        `,
        input: `
          block
          border
          disabled:bg-light-gray-500
          disabled:border-none
          disabled:cursor-not-allowed
          disabled:opacity-100
          disabled:text-black
          duration-100
          ease-in-out
          focus:outline-none
          focus:ring-0
          pl-4
          pr-10
          py-2
          rounded
          rounded-sm
          shadow-sm
          sm:py-3
          transition
          w-full
        `,
        wrapper: 'relative',
      },
      classes: {
        arrowWrapper: 'text-gray-700',
        input: 'bg-white border-primary-50 focus:border-primary-50 placeholder-gray-400 text-black',
      },
    },
  },
  't-tag': {
    component: TTag,
    props: {
      fixedClasses: 'px-2 rounded-full text-sm',
      classes: 'bg-secondary-500 text-xs text-white',
      variants: {
        scheduled: 'bg-secondary-300 text-black text-xs',
      },
    },
  },
};

Vue.use(VueTailwind, settings);
